import React, { createContext, useState, useContext, useEffect, useMemo, useRef } from "react";
import { useApiClient } from "../../../shared/customHooks";
import { useLocation, useNavigate, useParams } from "react-router";
import { PageHeader } from "../../header/PageHeader";
import { Role } from "../../../shared/enums/Role";
import { useGlobalContext } from "../../../shared/GlobalContext";
import { ToastInfo } from "../../toaster/toaster";
import { eventEmitter } from "../../../shared/event";
import { Menubar } from "primereact/menubar";
import { Modal, ModalRef } from "../../modal/modal";

// Create a Context
const ApplicationContext = createContext<IApplicationContext>({
  id: "",
  application: {},
  applicationBase: {},
  rehydrate: async () => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setApplication: (application: any) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setHeaderText: (headerText: IHeadetText) => {},
});

// Create a provider component
export const ApplicationContextProvider = ({ children }: any) => {
  const [isParentVersion, setIsParentVersion] = useState(false);
  const [application, setApplication] = useState<any>({});
  const [loaded, setLoaded] = useState(false);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const {
    accountInfo: { role },
  } = useGlobalContext();
  const { fetchData } = useApiClient(getController());

  const [headerText, setHeaderText] = useState<IHeadetText>({
    value: "Edit Type of Application",
    icon: "view-app-icon icon-spec mr-3 mt-0",
  });

  const navigate = useNavigate();

  useEffect(() => {
    getApplication(id ?? "");
  }, [id]);

  const modalRef = useRef<ModalRef | null>(null);

  function rehydrate(): Promise<void | never> {
    return getApplication(id ?? "");
  }

  function getController(): string {
    let controller = "";
    switch (role) {
      case Role.kindergarten:
        controller = "KindergartenBaseApplication";
        break;
      case Role.department:
        controller = "DepartmentApplication";
        break;
      default:
        controller = "BaseApplication";
    }
    return controller;
  }

  function getApplication(id: string): Promise<void | never> {
    let res: Promise<void | never>;
    switch (role) {
      case Role.assessor:
      case Role.manager:
      case Role.root: {
        res = fetchData(id).then(async (res) => {
          const url = res.data.applicationBase.discriminator + "/" + res.data.applicationBaseId;
          await getApplicationBase(res.data, url);
        });
        break;
      }
      case Role.provider: {
        res = fetchData(id).then(async (res) => {
          const url = "Provider" + res.data.applicationBase.discriminator + "/" + res.data.applicationBaseId;
          await getApplicationBase(res.data, url);
        });
        break;
      }
      case Role.kindergarten: {
        res = fetchData(id).then(async (res) => {
          setApplication(res.data);
          setLoaded(true);
        });
        break;
      }
      case Role.department: {
        res = fetchData(id)
          .then(async (res) => {
            setApplication(res.data);
            setLoaded(true);
          })
          .catch((error) => {
            if (error.status == 403) {
              navigate("/department/applications");
              const errorData: ToastInfo = {
                title: "Error",
                message: "You may not view presubmission applications",
                severity: "error",
              };
              eventEmitter.emit("tstr", errorData);
            }
            setLoaded(false);
          });
        break;
      }
      default: {
        res = Promise.reject(new Error("Invalid role"));
      }
    }
    return res;
  }

  async function getApplicationBase(resData: any, url: string) {
    const base = await fetchData(url);
    resData.applicationBase = base.data;
    setApplication(resData);

    const pathSegments = location.pathname.split("/");
    const otherDiscriminator = resData.applicationBase.discriminator == "ApplicationBaseCurrent" ? "ApplicationBaseLegacy" : "ApplicationBaseCurrent";
    const discrimIndex = pathSegments.indexOf(otherDiscriminator);
    if (discrimIndex != -1) {
      pathSegments[discrimIndex] = resData.applicationBase.discriminator;
      navigate(pathSegments.join("/"), { replace: true });
    }
    setLoaded(true);
  }

  const childInfo = useMemo(() => {
    if (application.applicationBase == null) return {};
    const { child, kindergarten } = application.applicationBase;
    return {
      name: `${child?.firstName} ${child?.lastName}`.trim(),
      kinder: kindergarten?.fundedKinder.accountName == "Other" ? kindergarten?.kindergartenNameOther : kindergarten?.fundedKinder.accountName,
    };
  }, [application]);

  function printApplication(parentVersion: boolean) {
    setIsParentVersion(parentVersion);
    modalRef.current?.toggle();
  }

  return (
    <div id="application-context" className={"w-full " + (isParentVersion && "parent-version")}>
      <ApplicationContext.Provider
        value={{
          id: id ?? "",
          application,
          applicationBase: application.applicationBase,
          rehydrate,
          setApplication,
          setHeaderText,
        }}>
        <PageHeader headerText={[headerText]} icon={"icon"}>
          <div className="student-info">
            {loaded && (
              <>
                <div className="flex justify-between items-start">
                  {" "}
                  <div className="flex-1 gap-2">
                    <div className="flex gap-4 justify-between items-start flex-wrap">
                      {" "}
                      <div className="appln-details">
                        {" "}
                        <span className="stu-details-icon icon-spec"></span>
                        <div className="text-purple mr-1">
                          <b>
                            {(() => {
                              if (application.applicationNumber > 0) {
                                return application.typeOfApplication != undefined && application.typeOfApplication == 0
                                  ? `Appeal ${application.applicationNumber}`
                                  : `Transfer ${application.applicationNumber}`;
                              } else if (application.hasAppeal) {
                                return "Appeal Received";
                              } else if (application.hasTransfer) {
                                return "Transfer Received";
                              }
                            })()}
                          </b>
                        </div>
                        <span className="mr-1">Application for:</span> <b className="text-left">{childInfo?.name}&nbsp;</b>
                        <div className="flex">
                          {" "}
                          <span className="mr-1">at kindergarten</span>
                          <b className="text-left">{childInfo?.kinder}</b>,<span className="ml-1">Reference ID:</span> <b>{id}</b>
                        </div>
                      </div>
                      {/* print button */}
                      {application?.applicationBase?.discriminator == "ApplicationBaseCurrent" && (
                        <div className="dont-show-in-print print-button print-list print-btn-width">
                          <Modal confirmAction={window.print} ref={modalRef} headerLabel="Print KIS Application" confirmLabel="Print">
                            <p>
                              You have selected to print the <span className="error">{isParentVersion ? "Parent Copy" : "Full KIS Application"}</span>
                              {isParentVersion ? " of the KIS application." : "."}
                            </p>
                            <p>
                              Click on the <b>Print</b> button to continue, or the <b>Cancel</b> button to select another option.
                            </p>
                          </Modal>
                          <Menubar
                            className="w-55"
                            model={[
                              {
                                label: "Print Application",
                                items: [
                                  {
                                    label: "Print Parent Copy",
                                    command: () => {
                                      printApplication(true);
                                    },
                                  },
                                  {
                                    label: "Print Full Application",
                                    command: () => {
                                      printApplication(false);
                                    },
                                  },
                                ],
                              },
                            ]}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </PageHeader>
        {loaded && children}
      </ApplicationContext.Provider>
    </div>
  );
};

interface IApplicationContext {
  id: string;
  application: any;
  applicationBase: any;
  rehydrate: () => Promise<void | never>;
  setApplication: (application: any) => void;
  setHeaderText: (headerText: IHeadetText) => void;
}

interface IHeadetText {
  value: string;
  icon: string;
  class?: string;
}
// Custom hook for using the context
export const useApplicationContext = (): IApplicationContext => useContext(ApplicationContext);
